<template>
  <div class="sa-voice" role="dialog" aria-modal="true" aria-label="Voice control">
    <!-- Header. Deliberately thin: the working area is the preview and the
         thumb zone, not the chrome. -->
    <header class="sa-voice-head">
      <p class="sa-voice-state" :class="'sa-is-' + uiState">
        <span class="sa-voice-state-icon" aria-hidden="true">
          <svg
            v-if="uiState == 'busy'"
            class="sa-voice-spin"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
          >
            <path d="M21 12a9 9 0 1 1-6.22-8.56" />
          </svg>
          <svg
            v-else-if="uiState == 'success'"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <svg
            v-else-if="uiState == 'warning'"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
          </svg>
        </span>
        <!-- A word as well as an icon and a colour, always. DESIGN.md 7. -->
        <span class="sa-voice-state-word">{{ stateWord }}</span>
      </p>

      <button
        type="button"
        class="sa-voice-close"
        aria-label="Close voice control"
        @click="requestClose"
      >
        &times;
      </button>
    </header>

    <main class="sa-voice-body">
      <!-- What the microphone heard. Shown verbatim and never rewritten: if the
           identifier came through wrong, the user needs to see that it did. -->
      <p v-if="transcript" class="sa-voice-heard">
        <span class="sa-voice-heard-label">Heard</span>
        &ldquo;{{ transcript }}&rdquo;
      </p>

      <!-- Preview. An `idle` surface styled as a form, not an alert
           (DESIGN.md 9). Nothing here has happened yet. -->
      <section v-if="preview" class="sa-voice-card" :class="{ 'sa-voice-card-destructive': isDestructive }">
        <h2 class="sa-voice-card-title">{{ intentTitle }}</h2>

        <!-- The destructive banner leads, before the detail rows, because a
             user scanning in gloves reads the first line and the buttons. -->
        <p v-if="isDestructive" class="sa-voice-destructive-banner">
          This permanently deletes the box.
          <template v-if="gridsLost > 0">
            {{ gridsLost }} non-empty grid{{ gridsLost == 1 ? "" : "s" }} will be
            lost.
          </template>
          <template v-else>It holds no non-empty grids.</template>
        </p>

        <dl class="sa-voice-rows">
          <template v-for="row in previewRows" :key="row.label">
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value }}</dd>
          </template>
        </dl>

        <p class="sa-voice-nothing-yet">Nothing has changed yet.</p>
      </section>

      <!-- Refusal. `warning`: understood and refused is the system working. -->
      <section v-else-if="refusal" class="sa-voice-refusal">
        <p class="sa-voice-refusal-title">{{ refusal.title }}</p>
        <p class="sa-voice-refusal-detail">{{ refusal.detail }}</p>
        <p class="sa-voice-refusal-next">{{ refusal.next }}</p>
      </section>

      <!-- Applied receipt. -->
      <section v-else-if="applied" class="sa-voice-applied">
        <p class="sa-voice-applied-title">{{ applied.title }}</p>
        <p class="sa-voice-applied-detail">{{ applied.detail }}</p>
      </section>

      <!-- Resting hint. Examples rather than instructions: a user in gloves
           copies a phrase, they do not read a grammar. -->
      <div v-else class="sa-voice-hint">
        <p class="sa-voice-hint-lead">Say what you want to do.</p>
        <ul class="sa-voice-hint-list">
          <li>&ldquo;Move box {{ hintBox }} to {{ hintPuck }} well seven.&rdquo;</li>
          <li>&ldquo;Create a box in {{ hintPuck }} well four.&rdquo;</li>
          <li>&ldquo;Delete box {{ hintBox }}.&rdquo;</li>
        </ul>
        <p class="sa-voice-hint-foot">
          Every command is shown here for you to confirm before anything changes.
        </p>
      </div>
    </main>

    <!-- Thumb zone. Everything tappable lives down here; the header close is a
         duplicate for reachability on a desk, not the primary way out. -->
    <footer class="sa-voice-foot">
      <!-- Signal-driven input meter. See DESIGN.md section 10: this moves
           because the microphone is moving it, and flatlines the moment audio
           stops, which is the property a fixed-rate animation cannot have. -->
      <div
        class="sa-voice-meter"
        :class="{ 'sa-voice-meter-live': meterLive }"
        role="img"
        :aria-label="meterLabel"
      >
        <span
          v-for="bar in meterBars"
          :key="bar"
          class="sa-voice-meter-bar"
          :class="{ 'sa-voice-meter-bar-on': bar <= meterLevel }"
        />
      </div>
      <p class="sa-voice-meter-caption">{{ meterLabel }}</p>

      <!-- Preview pending. Two separate blocks rather than one parameterised
           pair: which button destroys a sample is not a thing to work out from
           a ternary. -->
      <template v-if="preview && isDestructive">
        <!-- Order is deliberate. On a destructive command the button nearest the
             thumb's resting position at the bottom of the screen is the SAFE
             one, so a fumbled tap in gloves cancels rather than deletes. -->
        <button
          type="button"
          class="sa-voice-btn sa-voice-btn-danger"
          :disabled="busyAction"
          @click="confirmPreview"
        >
          {{ confirmLabel }}
        </button>
        <button
          type="button"
          class="sa-voice-btn sa-voice-btn-quiet"
          :disabled="busyAction"
          @click="cancelPreview"
        >
          Cancel
        </button>
      </template>

      <template v-else-if="preview">
        <button
          type="button"
          class="sa-voice-btn sa-voice-btn-primary"
          :disabled="busyAction"
          @click="confirmPreview"
        >
          {{ confirmLabel }}
        </button>
        <button
          type="button"
          class="sa-voice-btn sa-voice-btn-quiet"
          :disabled="busyAction"
          @click="cancelPreview"
        >
          Cancel
        </button>
      </template>

      <!-- Nothing pending: the only action is to stop. -->
      <button
        v-else
        type="button"
        class="sa-voice-btn sa-voice-btn-quiet"
        @click="requestClose"
      >
        Stop voice
      </button>
    </footer>
  </div>
</template>

<script>
import VoiceSession from "../../utils/voiceSession.js";

// Fallback destructive list. The server's /commands registry is authoritative,
// but if that fetch fails this must never make a confirmation LIGHTER than it
// should be — so the two are OR'd, never replaced.
const KNOWN_DESTRUCTIVE = ["delete_box"];

const INTENT_TITLES = {
  move_box: "Move a box",
  create_box: "Create a box",
  delete_box: "Delete a box",
};

const CONFIRM_LABELS = {
  move_box: "Confirm move",
  create_box: "Confirm create",
  delete_box: "Delete box",
};

const STATE_WORDS = {
  starting: "Starting",
  listening: "Listening",
  thinking: "Thinking",
  checking: "Checking",
  previewing: "Check this",
  applying: "Applying",
  applied: "Done",
  refused: "Not possible",
  stopped: "Stopped",
};

// DESIGN.md section 2, "Voice extension": these map onto the existing five
// states and add no sixth.
const STATE_UI = {
  starting: "busy",
  listening: "busy",
  thinking: "busy",
  checking: "busy",
  previewing: "idle",
  applying: "busy",
  applied: "success",
  refused: "warning",
  stopped: "idle",
};

const METER_BARS = 12;

function makeIdempotencyKey() {
  if (typeof crypto != "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `voice-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default {
  name: "VoiceCommandModal",
  emits: ["close"],
  data() {
    return {
      phase: "starting",
      transcript: "",
      preview: null,
      pendingCallId: null,
      pendingIntent: null,
      pendingFields: null,
      idempotencyKey: null,
      refusal: null,
      applied: null,
      busyAction: false,
      destructiveIntents: [...KNOWN_DESTRUCTIVE],
      meterLevel: 0,
      // Real transport facts, not derived from the phase word. The meter
      // caption must describe the microphone, and the microphone's state and
      // the label on the screen are two different things that can disagree.
      micOpen: false,
      // Set the instant this modal is torn down. `start()` awaits twice — the
      // token mint, then the socket — and during those gaps `this.session` may
      // not exist yet, so `unmounted`'s `session?.stop()` has nothing to stop.
      // Without this, closing mid-mint lets start() continue and open a
      // microphone on a modal that is already gone, with an upstream session
      // still billing and nothing holding a reference to it.
      teardown: false,
      micPaused: false,
      session: null,
      reducedMotion: false,
      lastMeterPaint: 0,
    };
  },
  computed: {
    stateWord() {
      return STATE_WORDS[this.phase] || "Voice";
    },
    uiState() {
      return STATE_UI[this.phase] || "idle";
    },
    intentTitle() {
      return INTENT_TITLES[this.pendingIntent] || "Confirm this command";
    },
    confirmLabel() {
      return CONFIRM_LABELS[this.pendingIntent] || "Confirm";
    },
    isDestructive() {
      return (
        !!this.pendingIntent &&
        this.destructiveIntents.includes(this.pendingIntent)
      );
    },
    gridsLost() {
      return Number(this.preview?.resolved?.nonEmptyGridCount) || 0;
    },
    // Labelled rows rather than the server's one-line summary: a scientist in
    // gloves verifies a destination well by finding the word "To", not by
    // parsing a sentence.
    previewRows() {
      const r = this.preview?.resolved;
      if (!r) return [];
      if (this.pendingIntent == "move_box") {
        return [
          { label: "Box", value: r.boxLabel },
          {
            label: "From",
            value: `${r.sourcePuckLabel} well ${r.sourceWell}`,
          },
          {
            label: "To",
            value: `${r.destinationPuckLabel} well ${r.destinationWell}`,
          },
        ];
      }
      if (this.pendingIntent == "create_box") {
        return [
          { label: "Box", value: r.boxLabel },
          {
            label: "Into",
            value: `${r.destinationPuckLabel} well ${r.destinationWell}`,
          },
        ];
      }
      if (this.pendingIntent == "delete_box") {
        return [
          { label: "Box", value: r.boxLabel },
          { label: "Location", value: `${r.puckLabel} well ${r.well}` },
          {
            label: "Non-empty grids",
            value: String(this.gridsLost),
          },
        ];
      }
      // An intent this build does not know about still gets the server's own
      // sentence rather than an empty card.
      return [{ label: "Command", value: this.preview.summary || "" }];
    },
    meterBars() {
      return METER_BARS;
    },
    meterLive() {
      return this.micOpen && !this.micPaused;
    },
    meterLabel() {
      if (!this.micOpen && this.phase == "starting")
        return "Opening the microphone";
      if (!this.micOpen) return "Microphone closed";
      if (this.micPaused && this.preview)
        return "Microphone paused while you check this";
      if (this.micPaused) return "Microphone paused";
      return "Microphone open";
    },
    // Example phrasing uses what is actually on screen where possible, so the
    // sentence the user copies contains an identifier that will resolve.
    hintPuck() {
      return this.$store.state.voiceScreenContext?.puckLabel || "the puck";
    },
    hintBox() {
      return this.$store.state.voiceScreenContext?.boxLabels?.[0] || "its name";
    },
  },
  methods: {
    /**
     * Identifiers the user can actually see. The API budget is 20 terms, so the
     * open puck and its boxes are sent rather than the whole dewar — recognition
     * hints for things off screen would push the on-screen ones out.
     */
    keyterms() {
      const ctx = this.$store.state.voiceScreenContext || {};
      return [ctx.puckLabel, ...(ctx.boxLabels || [])].filter(Boolean);
    },

    /** Single place that pauses audio, so the meter caption cannot drift. */
    mute(paused) {
      this.micPaused = !!paused;
      this.session?.setMuted(!!paused);
    },

    /**
     * Esc steps back one level rather than closing outright: with a preview on
     * screen it cancels the preview, and only an already-idle session is closed.
     * Bound on the document because this dialog does not take focus away from
     * whatever the user was doing behind it.
     */
    onKeydown(ev) {
      if (ev.key != "Escape") return;
      if (this.busyAction) return;
      if (this.preview) {
        ev.stopPropagation();
        this.cancelPreview();
        return;
      }
      this.requestClose();
    },

    async start() {
      // Not awaited and never blocking: if the registry is unreachable the
      // fallback list still marks delete_box destructive.
      this.$store
        .dispatch("fetchVoiceIntents")
        .then((intents) => {
          if (!Array.isArray(intents)) return;
          const fromServer = intents
            .filter((i) => i && i.destructive)
            .map((i) => i.name);
          this.destructiveIntents = [
            ...new Set([...KNOWN_DESTRUCTIVE, ...fromServer]),
          ];
        })
        .catch(() => {});

      let credentials;
      try {
        credentials = await this.$store.dispatch("mintVoiceSessionToken", {
          keyterms: this.keyterms(),
        });
      } catch {
        // mintVoiceSessionToken has already pushed the notification, including
        // the 402/403 distinction between "no minutes" and "not in the beta".
        this.$emit("close");
        return;
      }

      // Closed while the mint was in flight: never open the microphone.
      if (this.teardown) return;

      this.session = new VoiceSession({
        credentials,
        onEvent: this.onSessionEvent,
      });

      try {
        await this.session.start();
        // Closed while the socket was opening: the session is live now, so it
        // must be stopped explicitly rather than left for a hook that already ran.
        if (this.teardown) {
          this.session.stop("closed-during-start");
          return;
        }
        this.micOpen = true;
      } catch (err) {
        // `aborted` is not a failure: the transport threw it because THIS modal
        // was torn down mid-startup, and it already released whatever it held.
        // Telling the user "voice not started" for an action they themselves
        // took would be noise, and emitting close on an unmounted component is
        // pointless.
        if (err && err.voiceClass == "aborted") return;

        // The microphone is opened before the socket, so a socket failure can
        // leave a live capture behind. Release it here rather than relying on
        // unmount ordering — a hot mic in a shared lab is not a thing to leave
        // to a lifecycle hook.
        this.session.stop("failed-to-start");
        this.reportTransportFailure(err);
        this.$emit("close");
      }
    },

    reportTransportFailure(err) {
      const cls = err && err.voiceClass;
      if (cls == "micDenied") {
        this.$store.dispatch("notify", {
          uiState: "warning",
          title: "Voice not started.",
          detail: "Your browser did not allow microphone access.",
          next: "Allow the microphone for this site, then try again.",
          key: "voice|micDenied",
        });
        return;
      }
      if (cls == "unsupported") {
        this.$store.dispatch("notify", {
          uiState: "warning",
          title: "Voice not started.",
          detail: "This browser cannot open a microphone.",
          next: "Use Chrome or Safari on a secure connection.",
          key: "voice|unsupported",
        });
        return;
      }
      this.$store.dispatch("notify", {
        uiState: "danger",
        title: "Voice not started.",
        detail: "Could not connect to the voice service.",
        next: "Check your connection and try again.",
        key: "voice|transport",
      });
    },

    onSessionEvent(type, payload) {
      switch (type) {
        case "level":
          this.paintMeter(payload);
          break;

        case "listening":
          // A preview on screen owns the state word; a background turn ending
          // must not relabel it back to "Listening" under the user.
          if (!this.preview && !this.busyAction) this.phase = "listening";
          break;

        case "thinking":
          if (!this.preview && !this.busyAction) this.phase = "thinking";
          break;

        case "transcript":
          this.transcript = payload;
          break;

        case "proposal":
          this.onProposal(payload);
          break;

        case "model-error":
          // The model failing a turn is not the user's data failing. Say it
          // once, quietly, on the surface they are already looking at.
          this.refusal = {
            title: "That did not go through.",
            detail: "The voice service could not handle that request.",
            next: "Say it again, or stop voice and use the screen.",
          };
          this.phase = "refused";
          break;

        case "closed":
          this.onSessionClosed(payload);
          break;

        default:
          break;
      }
    },

    /**
     * Meter paint. Peak-with-decay so the bars are readable rather than
     * flickering, and throttled under reduced motion.
     */
    paintMeter(rms) {
      const now = Date.now();
      if (this.reducedMotion && now - this.lastMeterPaint < 200) return;
      this.lastMeterPaint = now;

      // Speech rms sits well under 1; this maps a useful range onto the bars
      // without a log scale nobody would be able to read at a glance.
      const target = Math.min(METER_BARS, Math.round(rms * 42));
      this.meterLevel =
        target >= this.meterLevel
          ? target
          : Math.max(target, this.meterLevel - 1);
    },

    /**
     * A tool call arrived. This runs PREVIEW and nothing else. There is no
     * branch here, or anywhere below it, that reaches apply.
     */
    async onProposal({ callId, intent, fields }) {
      if (this.preview || this.busyAction) {
        // Already holding a decision. Tell the model so, rather than replacing
        // what the user is currently reading.
        this.session?.respondToTool(
          callId,
          "A previous proposal is still awaiting the user's confirmation on screen."
        );
        return;
      }

      this.phase = "checking";
      this.refusal = null;
      this.applied = null;
      this.mute(true);

      let result;
      try {
        result = await this.$store.dispatch("previewVoiceCommand", {
          intent,
          fields,
        });
      } catch (err) {
        this.session?.noteRefusal();
        this.refusal = {
          title: "That command was refused.",
          // The store only populates `serverMessage` for validation and
          // conflict classes, so this cannot echo authorization wording.
          // Falling back to the frontend's own copy when it is absent.
          detail:
            (err && err.serverMessage) ||
            (err && err.detail) ||
            "The server would not accept that.",
          // Fixed, not the store's `next`. A preview writes nothing and
          // triggers no resync, so the write vocabulary's "the screen has been
          // refreshed" would be false here. The next step after a refusal is
          // always the same: say it differently, or use the screen.
          next: "Say it again with a different well or puck, or use the screen.",
        };
        this.phase = "refused";
        this.mute(false);
        this.session?.respondToTool(
          callId,
          "Refused. Nothing was changed. Tell the user briefly and ask them to try again."
        );
        return;
      }

      this.pendingCallId = callId;
      this.pendingIntent = intent;
      this.pendingFields = fields;
      // Minted with the preview, not with the tap, so a RETRY of the same
      // confirmed command carries the same key and cannot apply twice.
      //
      // It is `busyAction` that stops a double-tap, not this key — the key's job
      // is the case where we never learned the outcome and the user confirms
      // again. That only works because the failure path now keeps it.
      this.idempotencyKey = makeIdempotencyKey();
      this.preview = result;
      this.phase = "previewing";

      // The model is told what is on screen and, explicitly, that it is not
      // done. It must never announce a change that has not happened.
      this.session?.respondToTool(
        callId,
        `Proposed only: ${result.summary} The user must confirm on screen. Do not say it is done.`
      );

      // The session has done its job. From here the user is READING, not
      // speaking, and confirming is an ordinary HTTP call that never touches the
      // socket — so holding it open bills for a microphone nobody is talking
      // into, for as long as the person takes to decide. That is the single
      // largest avoidable cost in the flow, and muting alone did not fix it:
      // muting stops us SENDING audio, it does not stop the session existing.
      //
      // Closing here also means the common case never reaches the 90s ceiling or
      // the silence detector at all; both become backstops for the paths where
      // no proposal ever arrives.
      this.session?.stop("proposed");
    },

    /**
     * The one path to a write. Reachable only from a tap on a rendered button.
     */
    async confirmPreview() {
      if (!this.preview || this.busyAction) return;
      this.busyAction = true;
      this.phase = "applying";
      this.mute(true);

      try {
        await this.$store.dispatch("applyVoiceCommand", {
          intent: this.pendingIntent,
          fields: this.pendingFields,
          expectedPreviewHash: this.preview.previewHash,
          idempotencyKey: this.idempotencyKey,
        });

        this.session?.noteApplied();
        this.applied = {
          title: this.appliedTitle(),
          detail: this.preview.summary || "",
        };
        this.phase = "applied";
        this.clearPending();
      } catch (err) {
        const cls = (err && err.voiceClass) || "server";
        // `offline` and `server` mean WE NEVER LEARNED THE OUTCOME — the request
        // may well have committed and only the response was lost. On a phone in
        // a cold room, which is the device this modal exists for, that is the
        // likely failure. Saying "Box not saved." there states as fact the one
        // thing we do not know, and on a system whose whole premise is that the
        // user knows what happened to their sample, telling them the opposite of
        // the truth is the defect.
        const outcomeUnknown = cls == "offline" || cls == "server";

        this.$store.dispatch("notify", {
          uiState: err && err.uiState == "danger" ? "danger" : "warning",
          title: outcomeUnknown ? "Not sure that saved." : "Box not saved.",
          // `serverMessage` is only ever set for validation and conflict
          // classes (DESIGN.md section 5, "invalid": server message, else the
          // frontend's own wording).
          detail: outcomeUnknown
            ? "The server did not answer, so this may or may not have been applied."
            : (err && err.serverMessage) ||
              (err && err.detail) ||
              "The server would not accept that.",
          next: outcomeUnknown
            ? "Check the screen. If it did not happen, confirm again — retrying is safe."
            : (err && err.next) || "Check the screen, then try again.",
          key: `voice|apply|${cls}`,
        });

        if (outcomeUnknown) {
          // Keep the preview and — crucially — the SAME idempotency key, so
          // confirming again is a genuine retry the server can recognise rather
          // than a second, separate command. Clearing it here is what made the
          // retry the key exists for impossible.
          this.phase = "previewing";
        } else {
          this.phase = "listening";
          this.clearPending();
        }
      } finally {
        this.busyAction = false;
        this.mute(false);
      }
    },

    appliedTitle() {
      if (this.pendingIntent == "delete_box") return "Box deleted.";
      if (this.pendingIntent == "create_box") return "Box created.";
      if (this.pendingIntent == "move_box") return "Box moved.";
      return "Command applied.";
    },

    cancelPreview() {
      if (this.busyAction) return;
      const callId = this.pendingCallId;
      this.clearPending();
      this.phase = "listening";
      this.mute(false);
      this.session?.respondToTool(
        callId,
        "The user cancelled. Nothing was changed."
      );
    },

    clearPending() {
      this.preview = null;
      this.pendingCallId = null;
      this.pendingIntent = null;
      this.pendingFields = null;
      this.idempotencyKey = null;
    },

    requestClose() {
      // An apply already in flight is not cancelled by closing: the request is
      // with the server and the notification will report how it ended.
      this.session?.stop("user");
      this.$emit("close");
    },

    onSessionClosed({ reason, seconds, outcome }) {
      this.micOpen = false;
      this.micPaused = false;
      this.meterLevel = 0;

      this.$store.dispatch("reportVoiceSession", {
        id: this.session?.credentials?.voiceSessionId,
        reportedSeconds: seconds,
        outcome,
      });

      if (reason == "transport-lost") {
        // DESIGN.md section 9: transport loss is `danger`.
        this.$store.dispatch("notify", {
          uiState: "danger",
          title: "Voice stopped.",
          detail: "The connection to the voice service was lost.",
          next: "Start voice again to carry on.",
          key: "voice|transport-lost",
        });
        this.$emit("close");
        return;
      }

      // Ended by us because a proposal is on screen. Expected, not a failure,
      // and emphatically not a reason to take the card away.
      if (reason == "proposed") return;

      if (reason == "idle" || reason == "timeout") {
        this.phase = "stopped";

        // If a preview is on screen the user is MID-DECISION, and closing the
        // modal would take it away from them. The old comment here claimed
        // "nothing of the user's was lost", which was simply untrue in this case:
        // they are reading a confirmation and about to tap it.
        //
        // Confirming never needed the session. `applyVoiceCommand` is an ordinary
        // HTTP call and does not touch the socket, so ending the session stops
        // the billing WITHOUT costing the decision. Keep the card; the mic is off
        // and the state word says so.
        if (this.preview) return;

        // Nothing pending: no error, nothing lost, so no notification card —
        // only the state word changes.
        this.$emit("close");
      }
    },
  },

  mounted() {
    this.reducedMotion =
      typeof window.matchMedia == "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.onKeydown);
    this.start();
  },

  unmounted() {
    // FIRST, so anything still awaiting inside start() sees it and stops.
    this.teardown = true;
    document.body.style.overflow = "visible";
    document.removeEventListener("keydown", this.onKeydown);
    // Belt and braces: an unmount for any reason must release the microphone.
    this.session?.stop("unmount");
  },
};
</script>

<style scoped>
/* No Tailwind here. public/assets/styles.css is a committed, pre-compiled
   artefact that the Vue build does not regenerate, so a utility class not
   already in it silently does nothing. Same reasoning as AppNotifications.vue. */

/* Below AppNotifications (10000) on purpose: a failed write must stay readable
   over the top of this, not be buried by it. Above the nav overlay (9000). */
.sa-voice {
  position: fixed;
  inset: 0;
  z-index: 9500;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  color: #0c4a6e;
  font-family: Montserrat, sans-serif;
  text-align: left;
  overscroll-behavior: contain;
}

.sa-voice-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.5rem 0.5rem 0.875rem;
  border-bottom: 1px solid #e2e8f0;
}

.sa-voice-state {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.sa-voice-state-icon {
  line-height: 0;
}

.sa-is-busy {
  color: #0c4a6e;
}
.sa-is-idle {
  color: #0c4a6e;
}
.sa-is-success {
  color: #16a34a;
}
.sa-is-warning {
  color: #a16207;
}

/* The single long-running animation the design system already permits: an
   in-progress spinner, and only while something is genuinely in flight
   (DESIGN.md section 8). */
.sa-voice-spin {
  animation: sa-voice-rotate 900ms linear infinite;
}
@keyframes sa-voice-rotate {
  to {
    transform: rotate(360deg);
  }
}

.sa-voice-close {
  flex: none;
  min-width: 52px;
  min-height: 52px;
  border: 0;
  background: transparent;
  color: #0c4a6e;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}
.sa-voice-close:focus-visible {
  outline: 2px solid #0c4a6e;
  outline-offset: -2px;
}

.sa-voice-body {
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0.875rem;
}

.sa-voice-heard {
  margin: 0 0 0.75rem;
  font-size: 14px;
  line-height: 1.4;
  color: #475569;
}
.sa-voice-heard-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

/* A preview is a form, not an alert (DESIGN.md section 9). */
.sa-voice-card {
  border: 2px solid #0c4a6e;
  border-radius: 4px;
  padding: 0.875rem;
  background: #ffffff;
}
.sa-voice-card-destructive {
  border-color: rgb(248 113 113);
  background: #fef2f2;
}

.sa-voice-card-title {
  margin: 0 0 0.5rem;
  font-size: 16px;
  font-weight: 700;
}

.sa-voice-destructive-banner {
  margin: 0 0 0.75rem;
  padding: 0.5rem 0.625rem;
  border-left: 4px solid #ef4444;
  background: #ffffff;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
}

.sa-voice-rows {
  margin: 0;
  display: grid;
  grid-template-columns: minmax(5.5rem, auto) 1fr;
  gap: 0.375rem 0.75rem;
}
.sa-voice-rows dt {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
}
.sa-voice-rows dd {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.sa-voice-nothing-yet {
  margin: 0.75rem 0 0;
  font-size: 13px;
  color: #475569;
}

.sa-voice-refusal {
  border: 2px solid #facc15;
  border-radius: 4px;
  background: #fefce8;
  padding: 0.875rem;
}
.sa-voice-refusal-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.sa-voice-refusal-detail {
  margin: 0.125rem 0 0;
  font-size: 14px;
}
.sa-voice-refusal-next {
  margin: 0.25rem 0 0;
  font-size: 13px;
  color: #475569;
}

.sa-voice-applied {
  border: 2px solid #22c55e;
  border-radius: 4px;
  padding: 0.875rem;
}
.sa-voice-applied-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.sa-voice-applied-detail {
  margin: 0.125rem 0 0;
  font-size: 14px;
}

.sa-voice-hint-lead {
  margin: 0 0 0.5rem;
  font-size: 16px;
  font-weight: 600;
}
.sa-voice-hint-list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.sa-voice-hint-list li {
  margin-bottom: 0.5rem;
  font-size: 15px;
  line-height: 1.45;
  color: #334155;
}
.sa-voice-hint-foot {
  margin: 0.75rem 0 0;
  font-size: 13px;
  color: #475569;
}

/* Thumb zone. Padded for the home indicator so the last button is never half
   under it on a phone. */
.sa-voice-foot {
  flex: none;
  padding: 0.625rem 0.875rem calc(0.875rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

/* Signal-driven level meter — DESIGN.md section 10.
   Height is the readout, colour only corroborates it, and there is deliberately
   NO transition: bound 1 of the amendment requires the motion to stop in the
   same frame the signal does, and an eased height would keep moving after the
   microphone had gone quiet. Flicker is handled by peak-decay smoothing in the
   component, not by CSS easing. */
.sa-voice-meter {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 22px;
  margin-bottom: 0.25rem;
}
.sa-voice-meter-bar {
  flex: 1 1 0;
  /* Resting height. A meter reading zero is still a meter, not an empty box. */
  height: 25%;
  border-radius: 2px;
  background: #e2e8f0;
}
.sa-voice-meter-live .sa-voice-meter-bar-on {
  height: 100%;
  background: #0c4a6e;
}

.sa-voice-meter-caption {
  margin: 0 0 0.625rem;
  font-size: 12px;
  color: #475569;
}

/* 52 px: the preferred gloved-hand target, not the 44 px floor. */
.sa-voice-btn {
  display: block;
  width: 100%;
  min-height: 52px;
  margin-bottom: 0.625rem;
  padding: 0.75rem 1rem;
  border: 2px solid #0c4a6e;
  border-radius: 9999px;
  background: #ffffff;
  color: #0c4a6e;
  font-family: inherit;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
}
.sa-voice-btn:last-child {
  margin-bottom: 0;
}
.sa-voice-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.sa-voice-btn:focus-visible {
  outline: 3px solid #0c4a6e;
  outline-offset: 2px;
}

.sa-voice-btn-primary {
  background: #0c4a6e;
  color: #ffffff;
}
.sa-voice-btn-danger {
  background: #ef4444;
  border-color: #ef4444;
  color: #ffffff;
}
.sa-voice-btn-quiet {
  background: #ffffff;
  color: #0c4a6e;
}

/* Roomier on a tablet or desktop, but the layout does not change: this is one
   surface, designed for the phone that most people will hold. */
@media (min-width: 640px) {
  .sa-voice-body,
  .sa-voice-foot {
    max-width: 34rem;
    margin: 0 auto;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sa-voice-spin {
    animation: none;
  }
}
</style>
